/**
 * Order Queue Card - Redesigned (2-Tab System)
 *
 * Features:
 * - 2-Tab System: Active Orders + Completed
 * - Date Range Filter: Today, Yesterday, Last 7 Days, Last 30 Days
 * - Payment Status Badges: Paid (green), Pay at Pickup (yellow), Awaiting Payment (orange)
 * - Card view for Active Orders with Mark Complete button
 * - Compact table view for completed orders
 * - Search and filter capabilities
 * - Inline expand with invoice-style details
 * - Hover preview tooltip
 * - Full-screen expand option
 *
 * Author: Dine AI
 * Updated: December 15, 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import {
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2,
  Download,
  X,
  Phone,
  Clock,
  User,
  FileText,
  CheckCircle2,
  Undo2,
  Calendar,
  ChefHat,
  Bell
} from 'lucide-react';

// Date range options
type DateRange = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days';

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last_7_days: 'Last 7 Days',
  last_30_days: 'Last 30 Days'
};

// ==================== Types ====================

interface OrderQueueItem {
  order_id: number;
  customer_name: string | null;
  customer_phone: string;
  order_summary: string;
  total: number;
  status: string;
  payment_received: boolean;
  payment_link_sent: boolean;  // For badge logic
  created_at: string;
  estimated_ready_time: string | null;
  needs_review?: boolean;  // Large order flagged for staff review
  needs_review_reason?: string | null;  // Reason for flagging
  is_advance_order?: boolean;  // Advance order (future shift/next day)
  pickup_display?: string | null;  // "6:00 PM (Dinner)" or "12:00 PM tomorrow"
}

interface OrderQueue {
  active_orders: OrderQueueItem[];  // All orders not yet completed
  completed_orders: OrderQueueItem[];  // Orders completed in date range
  total_count: number;
  date_range: string;
  start_date: string;
  end_date: string;
  timezone?: string;  // Restaurant's IANA timezone (e.g. "America/New_York")
}

interface OrderItemDetail {
  item_id: number;
  item_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  special_instructions: string | null;
}

interface OrderDetail {
  order_id: number;
  customer_name: string | null;
  customer_phone: string;
  subtotal: number;
  tax: number;
  total: number;
  convenience_fee: number;
  pickup_time: string | null;
  is_advance_order?: boolean;
  pickup_display?: string | null;
  special_instructions: string | null;
  order_status: string;
  payment_received: boolean;
  created_at: string;
  items: OrderItemDetail[];
}

type TabType = 'active' | 'completed';

// ==================== Component ====================

const OrderQueueCard: React.FC = () => {
  const [queue, setQueue] = useState<OrderQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetail | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [toast, setToast] = useState<{ message: string; visible: boolean }>({ message: '', visible: false });
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>('today');
  const [showDateRangeDropdown, setShowDateRangeDropdown] = useState(false);

  /**
   * Fetch order queue from API
   */
  const fetchQueue = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    }

    // For manual refresh, ensure animation shows for at least 600ms
    const startTime = Date.now();

    try {
      const response = await fetch(`/api/virtual-host/control/order-queue?date_range=${selectedDateRange}`, {
        credentials: 'include'  // Send httpOnly cookie automatically
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order queue');
      }

      const data = await response.json();
      setQueue(data);
    } catch (error) {
      console.error('Error fetching order queue:', error);
    } finally {
      setLoading(false);

      // Ensure minimum animation time for manual refresh
      if (isManualRefresh) {
        const elapsed = Date.now() - startTime;
        const minAnimationTime = 600;
        if (elapsed < minAnimationTime) {
          await new Promise(resolve => setTimeout(resolve, minAnimationTime - elapsed));
        }
      }
      setRefreshing(false);
    }
  };

  /**
   * Handle manual refresh button click
   */
  const handleRefresh = () => {
    if (!refreshing) {
      fetchQueue(true);
    }
  };

  /**
   * Fetch order details for expand/hover
   */
  const fetchOrderDetails = async (orderId: number): Promise<OrderDetail | null> => {
    try {
      const response = await fetch(`/api/virtual-host/control/order-details/${orderId}`, {
        credentials: 'include'  // Send httpOnly cookie automatically
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching order details:', error);
      return null;
    }
  };

  /**
   * Handle row click to expand/collapse
   */
  const handleRowClick = async (orderId: number) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      setOrderDetails(null);
    } else {
      setExpandedOrderId(orderId);
      setLoadingDetails(true);
      const details = await fetchOrderDetails(orderId);
      setOrderDetails(details);
      setLoadingDetails(false);
    }
  };

  /**
   * Show toast notification (auto-dismisses after 3 seconds)
   */
  const showToast = (message: string) => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    setToast({ message, visible: true });

    // Auto-dismiss after 3 seconds
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ message: '', visible: false });
    }, 3000);
  };

  /**
   * Update order status manually
   */
  const updateOrderStatus = async (orderId: number, newStatus: string) => {
    try {
      const response = await fetch(`/api/virtual-host/control/update-order-status?order_id=${orderId}&new_status=${newStatus}`, {
        method: 'POST',
        credentials: 'include'  // Send httpOnly cookie automatically
      });

      if (!response.ok) {
        throw new Error('Failed to update order status');
      }

      await fetchQueue();

      // Show appropriate toast message based on status
      const statusMessages: Record<string, string> = {
        'preparing': `Order #${orderId} marked as Preparing`,
        'ready': `Order #${orderId} is Ready - SMS sent to customer`,
        'completed': `Order #${orderId} marked Complete`,
        'in_progress': `Order #${orderId} moved back to Active`
      };
      showToast(statusMessages[newStatus] || `Order #${orderId} status updated`);
    } catch (error) {
      console.error('Error updating order status:', error);
      showToast('Failed to update order. Please try again.');
    }
  };

  /**
   * Format phone number for display
   */
  const formatPhone = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  // Restaurant timezone from API response (falls back to browser timezone)
  const restaurantTimezone = queue?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  /**
   * Format timestamp for display (converts UTC to restaurant's timezone)
   * Note: Backend stores timestamps in UTC without timezone indicator,
   * so we append 'Z' to ensure JavaScript interprets it as UTC
   */
  const formatTime = (timestamp: string): string => {
    // Ensure timestamp is treated as UTC by appending 'Z' if not present
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(utcTimestamp);
    // Convert UTC timestamp to restaurant's timezone
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: restaurantTimezone
    });
  };

  /**
   * Get first item preview for table
   */
  const getFirstItemPreview = (summary: string): string => {
    if (!summary || summary === 'No summary') return '—';
    const firstItem = summary.split(',')[0].trim();
    return firstItem.length > 25 ? firstItem.substring(0, 25) + '...' : firstItem;
  };

  /**
   * Download invoice as text (could be enhanced to PDF)
   */
  const downloadInvoice = (order: OrderDetail) => {
    // Ensure timestamp is treated as UTC
    const utcTimestamp = order.created_at.endsWith('Z') ? order.created_at : order.created_at + 'Z';
    const lines = [
      `ORDER #${order.order_id}`,
      `Date: ${new Date(utcTimestamp).toLocaleDateString('en-US', { timeZone: restaurantTimezone })}`,
      `Time: ${formatTime(order.created_at)}`,
      '',
      `Customer: ${order.customer_name || 'N/A'}`,
      `Phone: ${formatPhone(order.customer_phone)}`,
      `Pickup: ${order.pickup_display || order.pickup_time || 'ASAP'}${order.is_advance_order ? ' (Advance Order)' : ''}`,
      '',
      '─'.repeat(40),
      'ITEMS',
      '─'.repeat(40),
      ...order.items.map(item =>
        `${item.quantity}x ${item.item_name}`.padEnd(30) + `$${item.total_price.toFixed(2)}`
      ),
      '',
      '─'.repeat(40),
      `Subtotal:`.padEnd(32) + `$${order.subtotal.toFixed(2)}`,
      `Convenience Fee:`.padEnd(32) + `$${order.convenience_fee.toFixed(2)}`,
      `Total:`.padEnd(32) + `$${order.total.toFixed(2)}`,
      '─'.repeat(40),
      '',
      order.special_instructions ? `Special Instructions: ${order.special_instructions}` : '',
      '',
      'Thank you for your order!'
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-${order.order_id}-invoice.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Normalize phone number for search (strip non-digits)
   */
  const normalizePhone = (phone: string): string => {
    return phone.replace(/\D/g, '');
  };

  // Filter orders by search query (smart phone search)
  const filterOrders = (orders: OrderQueueItem[]): OrderQueueItem[] => {
    if (!searchQuery.trim()) return orders;
    const query = searchQuery.toLowerCase();
    const queryDigits = normalizePhone(query); // For phone number matching

    return orders.filter(order => {
      // Match by order ID
      if (order.order_id.toString().includes(query)) return true;

      // Match by customer name
      if (order.customer_name && order.customer_name.toLowerCase().includes(query)) return true;

      // Smart phone match - compare digits only (handles +1, dashes, parentheses, spaces)
      if (queryDigits.length >= 3) {
        const orderPhoneDigits = normalizePhone(order.customer_phone);
        if (orderPhoneDigits.includes(queryDigits)) return true;
      }

      return false;
    });
  };

  // Initial load and auto-refresh (re-fetch when date range changes)
  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 10000);
    return () => clearInterval(interval);
  }, [selectedDateRange]);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  /**
   * Format current date and time for display
   */
  const formatDateTime = () => {
    return currentDateTime.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    }) + ' • ' + currentDateTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-2xl font-semibold text-gray-900 mb-6">Order Queue</h3>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!queue) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-2xl font-semibold text-gray-900 mb-6">Order Queue</h3>
        <p className="text-gray-500 text-center py-8">Failed to load order queue.</p>
      </div>
    );
  }

  // Current tab orders (filtered)
  const allFilteredOrders = filterOrders(
    activeTab === 'completed'
      ? queue.completed_orders
      : queue.active_orders
  );

  // Limit to 5 orders in dashboard view (show all in full screen)
  const DEFAULT_ORDER_LIMIT = 5;
  const currentOrders = isFullScreen ? allFilteredOrders : allFilteredOrders.slice(0, DEFAULT_ORDER_LIMIT);
  const hasMoreOrders = !isFullScreen && allFilteredOrders.length > DEFAULT_ORDER_LIMIT;

  // ==================== Render Components ====================

  /**
   * Badge component for tab counts
   */
  const CountBadge: React.FC<{ count: number; color: string }> = ({ count, color }) => (
    <span className={`
      ml-2 min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold
      flex items-center justify-center
      ${color}
    `}>
      {count}
    </span>
  );

  /**
   * ORDER STATUS BADGE - Order Lifecycle (New → Preparing → Ready → Done)
   * This is about fulfillment, NOT payment
   */
  const OrderStatusBadge: React.FC<{ order: OrderQueueItem }> = ({ order }) => {
    const status = (order.status || 'new').toLowerCase();

    const config: Record<string, { label: string; bg: string; text: string; icon: React.ReactNode }> = {
      'new': { label: 'New', bg: 'bg-blue-100', text: 'text-blue-700', icon: <FileText className="h-3 w-3" /> },
      'preparing': { label: 'Preparing', bg: 'bg-purple-100', text: 'text-purple-700', icon: <ChefHat className="h-3 w-3" /> },
      'ready': { label: 'Ready', bg: 'bg-green-100', text: 'text-green-700', icon: <Bell className="h-3 w-3" /> },
      'completed': { label: 'Done', bg: 'bg-gray-100', text: 'text-gray-600', icon: <CheckCircle2 className="h-3 w-3" /> },
    };

    // Default to 'new' for any unrecognized status (pay_at_pickup, awaiting_payment, etc.)
    const statusKey = config[status] ? status : 'new';
    const { label, bg, text, icon } = config[statusKey];

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
        {icon}
        {label}
      </span>
    );
  };

  /**
   * PAYMENT STATUS BADGE - Payment Lifecycle (Paid, Awaiting, Pay at Pickup, Needs Attention)
   * This is about money, NOT fulfillment
   * Derived from: payment_received, payment_link_sent, needs_review
   */
  const PaymentBadge: React.FC<{ order: OrderQueueItem }> = ({ order }) => {
    // Needs Attention takes priority (large order unpaid)
    if (order.needs_review && !order.payment_received) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
          ⚠ Needs Attention
        </span>
      );
    }
    if (order.payment_received) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
          ✓ Paid
        </span>
      );
    }
    if (order.payment_link_sent) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
          Awaiting Payment
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
        Pay at Pickup
      </span>
    );
  };

  /**
   * ADVANCE ORDER BADGE - Shows when order is for a future shift or next day
   */
  const AdvanceOrderBadge: React.FC<{ order: OrderQueueItem }> = ({ order }) => {
    if (!order.is_advance_order) return null;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
        <Clock className="h-3 w-3" />
        Advance Order
      </span>
    );
  };

  /**
   * Invoice detail panel (for expanded view) - includes status action buttons
   */
  const InvoicePanel: React.FC<{ order: OrderDetail; onStatusChange: (orderId: number, status: string) => void }> = ({ order, onStatusChange }) => {
    // Determine which buttons to show based on current status
    // Use inverse logic: hide button if already at or past that stage
    const currentStatus = (order.order_status || 'new').toLowerCase();

    // Order lifecycle: new → preparing → ready → completed
    const isPreparing = currentStatus === 'preparing';
    const isReady = currentStatus === 'ready';
    const isCompleted = currentStatus === 'completed';

    // Show Preparing if not already preparing, ready, or completed
    const showPreparing = !isPreparing && !isReady && !isCompleted;
    // Show Ready if not already ready or completed
    const showReady = !isReady && !isCompleted;
    // Show Complete if not already completed
    const showComplete = !isCompleted;

    return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-lg font-bold text-gray-900">Order #{order.order_id}</h4>
          <p className="text-sm text-gray-500">{new Date(order.created_at.endsWith('Z') ? order.created_at : order.created_at + 'Z').toLocaleDateString('en-US', { timeZone: restaurantTimezone })} at {formatTime(order.created_at)}</p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            downloadInvoice(order);
          }}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
        >
          <Download className="h-4 w-4" />
          Download
        </button>
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <User className="h-4 w-4 text-gray-400" />
          {order.customer_name || 'N/A'}
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <Phone className="h-4 w-4 text-gray-400" />
          {formatPhone(order.customer_phone)}
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <Clock className="h-4 w-4 text-gray-400" />
          Pickup: {order.pickup_display || order.pickup_time || 'ASAP'}
        </div>
      </div>

      {/* Items */}
      <div className="border-t border-gray-200 pt-3 mb-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-left">
              <th className="pb-2">Item</th>
              <th className="pb-2 text-center">Qty</th>
              <th className="pb-2 text-right">Price</th>
            </tr>
          </thead>
          <tbody className="text-gray-800">
            {order.items.map((item) => (
              <tr key={item.item_id} className="border-b border-gray-100">
                <td className="py-1.5">
                  {item.item_name}
                  {item.special_instructions && (
                    <span className="block text-xs text-gray-400 italic">{item.special_instructions}</span>
                  )}
                </td>
                <td className="py-1.5 text-center">{item.quantity}</td>
                <td className="py-1.5 text-right">${item.total_price.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="border-t border-gray-200 pt-3 space-y-1 text-sm">
        <div className="flex justify-between text-gray-600">
          <span>Subtotal</span>
          <span>${order.subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span>Convenience Fee</span>
          <span>${order.convenience_fee.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-gray-900 font-bold text-base pt-1">
          <span>Total</span>
          <span className="text-green-600">${order.total.toFixed(2)}</span>
        </div>
      </div>

      {/* Special Instructions */}
      {order.special_instructions && (
        <div className="mt-3 p-2 bg-gray-100 rounded text-sm">
          <span className="text-gray-500">Note: </span>
          <span className="text-gray-700">{order.special_instructions}</span>
        </div>
      )}

      {/* Status Action Buttons */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Current Status: <span className="font-medium text-gray-700 capitalize">{currentStatus.replace(/_/g, ' ')}</span>
          </span>
          <div className="flex gap-2">
            {showPreparing && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(order.order_id, 'preparing');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-lg text-sm font-medium transition-colors"
              >
                <ChefHat className="h-4 w-4" />
                Preparing
              </button>
            )}
            {showReady && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(order.order_id, 'ready');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg text-sm font-medium transition-colors"
              >
                <Bell className="h-4 w-4" />
                Ready
                <span className="text-xs opacity-70">(SMS)</span>
              </button>
            )}
            {showComplete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(order.order_id, 'completed');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
              >
                <CheckCircle2 className="h-4 w-4" />
                Complete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  };

  /**
   * Card view for Active Orders
   */
  const OrderCard: React.FC<{ order: OrderQueueItem }> = ({ order }) => (
    <div className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-4 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="font-semibold text-gray-900">Order #{order.order_id}</span>
            <PaymentBadge order={order} />
            <NeedsReviewBadge order={order} />
          </div>

          <div className="text-sm text-gray-600 space-y-1">
            <p><span className="text-gray-900 font-medium">Customer:</span> {order.customer_name || 'Name not provided'}</p>
            <p><span className="text-gray-900 font-medium">Phone:</span> {formatPhone(order.customer_phone)}</p>
            <p><span className="text-gray-900 font-medium">Time:</span> {formatTime(order.created_at)}</p>
            <p className="mt-2"><span className="text-gray-900 font-medium">Order:</span> {order.order_summary}</p>
          </div>
        </div>

        <div className="text-right ml-4 flex flex-col items-end">
          <p className="text-2xl font-bold text-green-600">${order.total.toFixed(2)}</p>

          {/* Checkmark icon with hover tooltip */}
          <button
            onClick={() => updateOrderStatus(order.order_id, 'completed')}
            className="mt-3 p-2 rounded-full text-gray-400 hover:text-green-600 hover:bg-green-100 transition-all group relative"
            title="Mark Complete"
          >
            <CheckCircle2 className="h-6 w-6" />
            {/* Tooltip on hover */}
            <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Mark Complete
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  // ==================== Main Render ====================

  const containerClass = isFullScreen
    ? 'fixed inset-0 z-50 bg-gray-50 overflow-auto'
    : 'bg-white rounded-xl border border-gray-200 shadow-sm';

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className={`border-b border-gray-200 px-6 py-5 ${isFullScreen ? 'sticky top-0 bg-white shadow-sm z-10' : ''}`}>
        <div className={`flex items-center justify-between ${isFullScreen ? 'max-w-6xl mx-auto' : ''}`}>
          <div className="flex items-baseline gap-3">
            <h3 className="text-2xl font-semibold text-gray-900">Order Queue</h3>
            <span className="text-sm text-gray-400">{formatDateTime()}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Search Box */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 text-sm w-48 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Date Range Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowDateRangeDropdown(!showDateRangeDropdown)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-700 text-sm transition-colors"
              >
                <Calendar className="h-4 w-4 text-gray-500" />
                <span>{DATE_RANGE_LABELS[selectedDateRange]}</span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDateRangeDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showDateRangeDropdown && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDateRangeDropdown(false)}
                  />
                  {/* Dropdown menu */}
                  <div className="absolute top-full right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-xl z-20 overflow-hidden">
                    {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((range) => (
                      <button
                        key={range}
                        onClick={() => {
                          setSelectedDateRange(range);
                          setShowDateRangeDropdown(false);
                        }}
                        className={`
                          w-full px-4 py-2.5 text-left text-sm transition-colors
                          ${selectedDateRange === range
                            ? 'bg-blue-600 text-white font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                          }
                        `}
                      >
                        {DATE_RANGE_LABELS[range]}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <span className="text-sm text-gray-500">Total: {queue.total_count} orders</span>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>

            {/* Full Screen Toggle - subtle pulse animation to draw attention */}
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className={`
                p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all
                ${!isFullScreen && hasMoreOrders ? 'animate-pulse ring-2 ring-blue-400' : ''}
              `}
              title={isFullScreen ? 'Exit Full Screen' : `Full Screen (${allFilteredOrders.length} orders)`}
            >
              {isFullScreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>

            {isFullScreen && (
              <button
                onClick={() => setIsFullScreen(false)}
                className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation - 2 tabs: Active Orders + Completed Today */}
      <div className="border-b border-gray-200 px-6">
        <div className={`flex gap-2 ${isFullScreen ? 'max-w-6xl mx-auto' : ''}`}>
          {/* Active Orders Tab */}
          <button
            onClick={() => setActiveTab('active')}
            className={`
              px-4 py-3 font-medium text-sm transition-all rounded-t-lg flex items-center
              ${activeTab === 'active'
                ? 'bg-[#FDB913] text-black'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }
            `}
          >
            Active Orders
            <CountBadge
              count={queue.active_orders.length}
              color={activeTab === 'active' ? 'bg-black/20 text-black' : 'bg-yellow-100 text-yellow-700'}
            />
          </button>

          {/* Completed Tab - label changes based on date range */}
          <button
            onClick={() => setActiveTab('completed')}
            className={`
              px-4 py-3 font-medium text-sm transition-all rounded-t-lg flex items-center
              ${activeTab === 'completed'
                ? 'bg-[#FDB913] text-black'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }
            `}
          >
            Completed
            <CountBadge
              count={queue.completed_orders.length}
              color={activeTab === 'completed' ? 'bg-black/20 text-black' : 'bg-green-100 text-green-700'}
            />
          </button>
        </div>
      </div>

      {/* Order List */}
      <div className={`p-6 ${isFullScreen ? 'max-w-6xl mx-auto' : ''}`}>
        {currentOrders.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {searchQuery
                ? 'No orders match your search'
                : activeTab === 'active'
                  ? 'No active orders'
                  : `No completed orders for ${DATE_RANGE_LABELS[selectedDateRange].toLowerCase()}`}
            </p>
          </div>
        ) : (
          /* Compact Table View for Both Tabs */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-500 text-sm border-b border-gray-200">
                  <th className="pb-3 font-medium">Order #</th>
                  {activeTab === 'active' && <th className="pb-3 font-medium">Status</th>}
                  <th className="pb-3 font-medium">Customer</th>
                  <th className="pb-3 font-medium">Phone</th>
                  <th className="pb-3 font-medium">Time</th>
                  <th className="pb-3 font-medium">Items</th>
                  <th className="pb-3 font-medium text-right">Total</th>
                  <th className="pb-3 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {currentOrders.map((order) => (
                  <React.Fragment key={order.order_id}>
                    {/* Table Row */}
                    <tr
                      className={`
                        border-b border-gray-100 cursor-pointer transition-colors
                        ${expandedOrderId === order.order_id ? 'bg-blue-50' : 'hover:bg-gray-50'}
                      `}
                      onClick={() => handleRowClick(order.order_id)}
                    >
                      <td className="py-3 text-gray-900 font-medium">#{order.order_id}</td>
                      {activeTab === 'active' && (
                        <td className="py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <OrderStatusBadge order={order} />
                            <PaymentBadge order={order} />
                            <AdvanceOrderBadge order={order} />
                          </div>
                        </td>
                      )}
                      <td className="py-3 text-gray-700">{order.customer_name || '—'}</td>
                      <td className="py-3 text-gray-600 text-sm">{formatPhone(order.customer_phone)}</td>
                      <td className="py-3 text-gray-600 text-sm">{formatTime(order.created_at)}</td>
                      <td className="py-3 text-gray-500 text-sm">{getFirstItemPreview(order.order_summary)}</td>
                      <td className="py-3 text-green-600 font-semibold text-right">${order.total.toFixed(2)}</td>
                      <td className="py-3 text-gray-400">
                        {expandedOrderId === order.order_id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </td>
                    </tr>

                    {/* Expanded Invoice Detail */}
                    {expandedOrderId === order.order_id && (
                      <tr>
                        <td colSpan={activeTab === 'active' ? 8 : 7} className="p-4 bg-gray-50">
                          {loadingDetails ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            </div>
                          ) : orderDetails ? (
                            <InvoicePanel order={orderDetails} onStatusChange={updateOrderStatus} />
                          ) : (
                            <p className="text-gray-500 text-center py-4">Failed to load order details</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* View All button when there are more orders */}
            {hasMoreOrders && (
              <button
                onClick={() => setIsFullScreen(true)}
                className="w-full mt-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-600 hover:text-gray-900 text-sm font-medium transition-all flex items-center justify-center gap-2"
              >
                <Maximize2 className="h-4 w-4" />
                View all {allFilteredOrders.length} orders in full screen
              </button>
            )}
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toast.visible && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="flex items-center gap-3 px-4 py-3 bg-green-500/90 text-white rounded-lg shadow-lg backdrop-blur-sm">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderQueueCard;
